import { useDispatch, useSelector } from "react-redux";
import Footer from "../../SharedModule/components/Footer";
import { verifyModuleAccess } from "../../SharedModule/utils/accessUtils";
import { Spinner } from "../../SharedModule/components/Spinner";
import {
  CLIENTS_ALL_ROLES,
  ROUTE_CLIENTS,
  ROUTE_CLIENTS_RELATIONSHIP_KEY_CONTACTS,
  ROUTE_CLIENTS_RELATIONSHIP_KEY_CONTACT_DETAILS,
  ROUTE_CLIENTS_RELATIONSHIP_KEY_CONTACTS_CONFIDENCE_HISTORY,
  ROUTE_CLIENTS_CONTACTS_PROFILE,
  ROLE_SUPER_ADMIN,
  ROLE_CLIENT_ADMIN,
  ROLE_BUSINESS_DEVELOPMENT_LEAD,
  ROLE_SUPERVISOR,
  ROUTE_CLIENTS_RELATIONSHIP_REVIEW,
} from "../../SharedModule/utils/constants";
import { ForbiddenPage } from "../../SharedModule/pages/ForbiddenPage";
import { useNavigate, useParams, Link, useLocation } from "react-router";
import { setActiveTab } from "../../SharedModule/redux/reducers/uiShared.reducer";
import { useEffect, useRef, useState } from "react";
import HeaderClientsModule from "../components/HeaderClientsModule";
import { BiInfoCircle, BiLeftArrowAlt } from "react-icons/bi";
import { useModal } from "../../SharedModule/hooks/useModal";
import {
  ClientKeyContactDetailForm,
  AdvocacyLevel,
  StakeholderConfidence,
} from "../types/clients-module-types";
import { useClientDeleteKeyContact } from "../hooks/useClientDeleteKeyContact";
import { ModalPortal } from "../../SharedModule/components/ModalPortal";
import { ModalImage } from "../../SharedModule/components/ModalImage";
import { useClientKeyContactDetails } from "../hooks/useClientKeyContactDetails";
import { FiMail, FiPhoneCall } from "react-icons/fi";
import { Select } from "../../SharedModule/components/Select";
import { Calendar } from "../../SharedModule/components/Calendar";
import { Slider } from "@mui/material";
import {
  setAdvocacyDescription,
  setAdvocacyTexts,
} from "../utils/contact-advocacy.factory";
import {
  getAdvocacyNumber,
  getUserNameToCompleteSelect,
  getTrendingName,
  getRelationshipTrend,
} from "../utils/keyContactUtils";
import {
  getEmptyNewKeyContactForm,
  loadFirstStateAdvocacyLevel,
  loadFirstStateStakeholderConfidence,
  loadKeyContactForm,
} from "./../utils/clientsUtils";
import TextareaAutosize from "@mui/material/TextareaAutosize";
import { ClientsService } from "../services/clients";
import { selectMetaLoaded } from "../../BillingModule/redux/reducers/metaBilling.reducer";
import { useBillingMetadata } from "../../BillingModule/hooks/useBillingMetadata";
import { addToast } from "../../SharedModule/redux/reducers/ui.reducer";
import eventHandler from "../../SharedModule/utils/eventHandler";
import {
  setContact,
  setNeedUpdateKeyContactList,
} from "../redux/reducers/clientKeyContacts.reducer";
import { resetScrollY } from "../../SharedModule/utils/pagesUtils";

export const ClientKeyContactDetailPage = () => {
  useBillingMetadata();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // keep this import. We use location.pathname as param in useEffect to re render this component after "save & view next"
  const location = useLocation();
  const metaBillingLoaded = useSelector(selectMetaLoaded);

  const { id, relationshipReviewId, keyContactId } = useParams();

  const isEditMode = !location.pathname.endsWith("newKeyContact");

  // If there is no id, go to clients list
  if (!id) navigate(ROUTE_CLIENTS);

  let routeBack = ROUTE_CLIENTS_RELATIONSHIP_KEY_CONTACTS.replace(
    ":id",
    id ?? ""
  ).replace(":relationshipReviewId", relationshipReviewId ?? "");

  if (!relationshipReviewId)
    navigate(routeBack, {
      state: {
        from:
          location.state?.from === ROUTE_CLIENTS_RELATIONSHIP_REVIEW
            ? ROUTE_CLIENTS_RELATIONSHIP_REVIEW
            : "",
      },
    });

  const {
    ClientForHeaderInfo,
    auth,
    activeUserId,
    activeUserName,
    users,
    isLoadingKeyContacts,
    keyContacts,
    userRolesList,
    rolesLoaded,
    today,
    trendingOptions,
    contact,
    actualKeyContactData,
    isLoadingActualKeyContactData,
    loadKeyContactDetails,
    setIsLoadingActualKeyContactData,
    costCenterOwnedIds,
    actualCompanyOwnerUserId,
    clientRelationshipReview,
  } = useClientKeyContactDetails(
    Number(id),
    Number(relationshipReviewId),
    keyContactId ? Number(keyContactId) : null,
    routeBack
  );

  // Add mode and not contact defined, when user enter by url
  if (!isEditMode && !contact) {
    navigate(routeBack);
  }

  // Set actual user id to apply on stakeholderLastVerifiedBy when any fields change
  let myself: any = {
    value: activeUserId,
    label: activeUserName,
  };

  useEffect(() => {
    document.title = "The Hub - Key Contact Details";

    dispatch(setActiveTab(ROUTE_CLIENTS_RELATIONSHIP_KEY_CONTACTS));

    return () => {
      resetScrollY(globalThis.location.pathname);
    };
  }, []);

  const isLogged = auth.isAuthenticated;

  const [formTouched, setFormTouched] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Modal config
  const modalRef = useRef<undefined>(undefined);
  const showModalProfiles = () => {
    (modalRef.current as any).show(true);
  };
  const closeModalProfiles = () => {
    (modalRef.current as any).show(false);
  };

  useEffect(() => {
    // avoid issue on load previus key contact data
    setKeyContactForm(
      isEditMode
        ? loadKeyContactForm(actualKeyContactData, today, myself.value)
        : getEmptyNewKeyContactForm(today, myself.value)
    );
    setIsUserPrimaryRelationshipSteward(
      myself.value === actualKeyContactData.primaryRelationshipStewardId
    );
    setFirstStateStakeholderConfidence(
      loadFirstStateStakeholderConfidence(
        actualKeyContactData,
        today,
        myself.value,
        isEditMode
      )
    );
    setFirstStateAdvocacyLevel(
      loadFirstStateAdvocacyLevel(actualKeyContactData, today, isEditMode)
    );
  }, [actualKeyContactData]);

  // Form to edit key contact
  const [keyContactForm, setKeyContactForm] =
    useState<ClientKeyContactDetailForm>(
      isEditMode
        ? loadKeyContactForm(actualKeyContactData, today, myself.value)
        : getEmptyNewKeyContactForm(today, myself.value)
    );
  // Form Validation
  const [isFormValid, setIsFormValid] = useState(!isEditMode);

  const [
    isUserPrimaryRelationshipSteward,
    setIsUserPrimaryRelationshipSteward,
  ] = useState(
    myself.value === actualKeyContactData.primaryRelationshipStewardId
  );

  // Set first state of Advocacy Level / Stakeholder Confidence
  // If new value is equals to this first value(slider), all data set at first moment values
  const [firstStateStakeholderConfidence, setFirstStateStakeholderConfidence] =
    useState<StakeholderConfidence>(
      loadFirstStateStakeholderConfidence(
        actualKeyContactData,
        today,
        myself.value,
        isEditMode
      )
    );

  const [firstStateAdvocacyLevel, setFirstStateAdvocacyLevel] =
    useState<AdvocacyLevel>(
      loadFirstStateAdvocacyLevel(actualKeyContactData, today, isEditMode)
    );

  // Form fields touched
  const [formFields, setFormFields] = useState({
    relationshipGrowthPlan: false,
    advocacyLevel: false,
    stakeholderConfidence: false,
  });

  let confidenceHistoryLink: string =
    ROUTE_CLIENTS_RELATIONSHIP_KEY_CONTACTS_CONFIDENCE_HISTORY.replace(
      ":id",
      id ?? ""
    )
      .replace(":relationshipReviewId", relationshipReviewId ?? "")
      .replace(":keyContactId", keyContactId ?? "");

  const { showModal, closeModal, Modal, ...modalProps } = useModal();

  const clientDeleteKeyContact = useClientDeleteKeyContact(
    showModal,
    closeModal,
    setActionLoading
  );

  const checkFormValid = () => {
    if (
      keyContactForm.stakeholderClientComment.length === 0 &&
      formFields.stakeholderConfidence
    ) {
      setIsFormValid(false);
    } else {
      setIsFormValid(true);
    }
  };

  // Primary Relationship StewardId
  const handlePrimaryRelationshipStewardIdChange = (actualValueToUpdate) => {
    setKeyContactForm({
      ...keyContactForm,
      primaryRelationshipStewardId: actualValueToUpdate,
    });
  };

  // Behaviors And Concerns
  const handleBehaviorsAndConcernsChange = (actualValueToUpdate) => {
    setKeyContactForm({
      ...keyContactForm,
      behaviorsAndConcerns: actualValueToUpdate,
      relationshipLastVerified: today,
    });
    setFormFields({
      ...formFields,
      relationshipGrowthPlan: true,
    });
  };

  // Relationship Growth Plan
  const handleRelationshipGrowthPlanChange = (actualValueToUpdate) => {
    setKeyContactForm({
      ...keyContactForm,
      relationshipGrowthPlan: actualValueToUpdate,
      relationshipLastVerified: today,
    });
    setFormFields({
      ...formFields,
      relationshipGrowthPlan: true,
    });
  };

  const handleRelationshipLastVerifiedChange = (actualValueToUpdate) => {
    setKeyContactForm({
      ...keyContactForm,
      relationshipLastVerified: new Date(actualValueToUpdate),
    });
    setFormFields({
      ...formFields,
      relationshipGrowthPlan: true,
    });
  };

  // Advocacy Level
  const handleAdvocacyLevelChange = (actualValueToUpdate) => {
    // check new value is equal from BE, we should set this field as "untouched"
    if (actualValueToUpdate === firstStateAdvocacyLevel.level) {
      setKeyContactForm({
        ...keyContactForm,
        advocacyLevel: firstStateAdvocacyLevel.level,
        advocacyTrending: firstStateAdvocacyLevel.relationshipLevelTrendId,
        advocacyLastVerified: new Date(firstStateAdvocacyLevel.lastVerified),
      });
      setFormFields({
        ...formFields,
        advocacyLevel: false,
      });
    } else {
      setKeyContactForm({
        ...keyContactForm,
        advocacyLevel: actualValueToUpdate,
        advocacyTrending: 3,
        advocacyLastVerified: today,
      });
      setFormFields({
        ...formFields,
        advocacyLevel: true,
      });
    }
  };

  // Relationship Level Trending
  const handleRelationshipLevelTrendIdChange = (actualValueToUpdate) => {
    setKeyContactForm({
      ...keyContactForm,
      advocacyTrending: actualValueToUpdate,
      advocacyLastVerified: today,
    });
    setFormFields({
      ...formFields,
      advocacyLevel: true,
    });
  };

  const handleAdvocacyLastVerifiedChange = (actualValueToUpdate) => {
    setKeyContactForm({
      ...keyContactForm,
      advocacyLastVerified: new Date(actualValueToUpdate),
    });
    setFormFields({
      ...formFields,
      advocacyLevel: true,
    });
  };

  // Stakeholder Confidence
  const handleStakeholderConfidenceChange = (actualValueToUpdate) => {
    let formValidAlreadyChecked = false;
    // check new value is equal from BE, we should set this field as "untouched"
    if (
      actualValueToUpdate === firstStateStakeholderConfidence.confidenceValue
    ) {
      setIsFormValid(true);
      setKeyContactForm({
        ...keyContactForm,
        stakeholderConfidence: firstStateStakeholderConfidence.confidenceValue,
        stakeholderClientComment:
          firstStateStakeholderConfidence.clientComment,
        stakeholderLastVerified: new Date(
          firstStateStakeholderConfidence.lastVerified
        ),
        stakeholderLastVerifiedBy:
          firstStateStakeholderConfidence.lastVerifiedBy,
      });
      setFormFields({
        ...formFields,
        stakeholderConfidence: false,
      });
    } else {
      setFormFields({
        ...formFields,
        stakeholderConfidence: true,
      });
      setKeyContactForm({
        ...keyContactForm,
        stakeholderConfidence: actualValueToUpdate,
        stakeholderLastVerified: today,
        stakeholderLastVerifiedBy: myself.value,
      });
      // check if form is valid when changes level
      if (keyContactForm.stakeholderClientComment.length === 0) {
        setIsFormValid(false);
        formValidAlreadyChecked = true;
      }
    }
    return formValidAlreadyChecked;
  };

  const handleStakeholderClientCommentChange = (actualValueToUpdate) => {
    setFormFields({
      ...formFields,
      stakeholderConfidence: true,
    });
    setKeyContactForm({
      ...keyContactForm,
      stakeholderClientComment: actualValueToUpdate,
      stakeholderLastVerified: today,
      stakeholderLastVerifiedBy: myself.value,
    });
    // check if form is valid when changes comment
    if (actualValueToUpdate.length === 0) {
      setIsFormValid(false);
    } else {
      setIsFormValid(true);
    }
    return true;
  };

  const handleStakeholderLastVerifiedOrByChange = (
    property: string,
    actualValueToUpdate
  ) => {
    let formValidAlreadyChecked = false;
    setFormFields({
      ...formFields,
      stakeholderConfidence: true,
    });
    if (property === "stakeholderLastVerified") {
      setKeyContactForm({
        ...keyContactForm,
        stakeholderLastVerified: new Date(actualValueToUpdate),
      });
    } else {
      setKeyContactForm({
        ...keyContactForm,
        stakeholderLastVerifiedBy: actualValueToUpdate,
      });
    }
    // check if form is valid when changes last verified
    if (keyContactForm.stakeholderClientComment.length === 0) {
      setIsFormValid(false);
      formValidAlreadyChecked = true;
    }
    return formValidAlreadyChecked;
  };

  const handleFormChange = (e: any, property: string) => {
    let actualValueToUpdate = typeof e === "object" ? e.target.value : e;
    let formValidAlreadyChecked = false;

    // Flag to know if there is one change on form, to avoid save data when "Save & View Next Contact" is clicked
    setFormTouched(true);

    // Validations
    if (property === "primaryRelationshipStewardId") {
      handlePrimaryRelationshipStewardIdChange(actualValueToUpdate);
    } else if (property === "behaviorsAndConcerns") {
      handleBehaviorsAndConcernsChange(actualValueToUpdate);
    } else if (property === "relationshipGrowthPlan") {
      handleRelationshipGrowthPlanChange(actualValueToUpdate);
    } else if (property === "relationshipLastVerified") {
      handleRelationshipLastVerifiedChange(actualValueToUpdate);
    } else if (property === "advocacyLevel") {
      handleAdvocacyLevelChange(actualValueToUpdate);
    } else if (property === "relationshipLevelTrendId") {
      handleRelationshipLevelTrendIdChange(actualValueToUpdate);
    } else if (property === "advocacyLastVerified") {
      handleAdvocacyLastVerifiedChange(actualValueToUpdate);
    } else if (property === "stakeholderConfidence") {
      formValidAlreadyChecked = handleStakeholderConfidenceChange(
        actualValueToUpdate
      );
    } else if (property === "stakeholderClientComment") {
      formValidAlreadyChecked = handleStakeholderClientCommentChange(
        actualValueToUpdate
      );
    } else if (
      property === "stakeholderLastVerified" ||
      property === "stakeholderLastVerifiedBy"
    ) {
      formValidAlreadyChecked = handleStakeholderLastVerifiedOrByChange(
        property,
        actualValueToUpdate
      );
    }

    // check if form is valid
    if (!formValidAlreadyChecked) {
      checkFormValid();
    }
  };

  const customNavigate = (viewNextKeyContact: boolean) => {
    // Go to next key contact in list
    if (keyContacts && viewNextKeyContact) {
      let actualKeyContactIndex = keyContacts.findIndex(
        (elem) => Number(keyContactId) === elem.id
      );
      // if last key contact in element list, go to key contact list
      if (actualKeyContactIndex === keyContacts.length - 1) {
        navigate(routeBack, {
          state: {
            from:
              location.state?.from === ROUTE_CLIENTS_RELATIONSHIP_REVIEW
                ? ROUTE_CLIENTS_RELATIONSHIP_REVIEW
                : "",
          },
        });
      } else {
        // set form as untouched to disabled 'save changes' button
        setFormTouched(false);
        dispatch(setIsLoadingActualKeyContactData(true));
        loadKeyContactDetails(keyContacts[actualKeyContactIndex + 1].id);
        navigate(
          ROUTE_CLIENTS_RELATIONSHIP_KEY_CONTACT_DETAILS.replace(
            ":id",
            id ?? ""
          )
            .replace(":relationshipReviewId", relationshipReviewId ?? "")
            .replace(
              ":keyContactId",
              keyContacts[actualKeyContactIndex + 1].id.toString()
            ),
          {
            state: {
              from:
                location.state?.from === ROUTE_CLIENTS_RELATIONSHIP_REVIEW
                  ? ROUTE_CLIENTS_RELATIONSHIP_REVIEW
                  : "",
            },
          }
        );
      }
    } else {
      // go to key contact list
      navigate(routeBack, {
        state: {
          from:
            location.state?.from === ROUTE_CLIENTS_RELATIONSHIP_REVIEW
              ? ROUTE_CLIENTS_RELATIONSHIP_REVIEW
              : "",
        },
      });
    }
  };

  const handleAddKeyContact = async (event: any) => {
    event.preventDefault();
    setActionLoading(true);

    let body: any = {
      contactId: contact.id,
      primaryRelationshipStewardId: keyContactForm.primaryRelationshipStewardId,
      relationshipGrowthPlan: formFields.relationshipGrowthPlan
        ? {
            growthPlan: keyContactForm.relationshipGrowthPlan,
            behaviorsAndConcerns: keyContactForm.behaviorsAndConcerns,
            lastVerified:
              keyContactForm.relationshipLastVerified?.toISOString(),
          }
        : {
            growthPlan: "",
            behaviorsAndConcerns: "",
            lastVerified: today.toISOString(),
          },
      advocacyLevel: formFields.advocacyLevel
        ? {
            // ASUITE-2107: N/A in Database has a new id equal to 5 to allow save
            level:
              keyContactForm.advocacyLevel === 0
                ? 5
                : keyContactForm.advocacyLevel,
            relationshipLevelTrendId: keyContactForm.advocacyTrending,
            lastVerified: keyContactForm.advocacyLastVerified?.toISOString(),
          }
        : {
            // ASUITE-2107: N/A in Database has a new id equal to 5 to allow save
            level: 5,
            relationshipLevelTrendId: 3,
            lastVerified: today.toISOString(),
          },
      stakeholderConfidence: formFields.stakeholderConfidence
        ? {
            confidenceValue: keyContactForm.stakeholderConfidence,
            clientComment: keyContactForm.stakeholderClientComment,
            lastVerified: keyContactForm.stakeholderLastVerified?.toISOString(),
            lastVerifiedBy: keyContactForm.stakeholderLastVerifiedBy,
          }
        : {
            confidenceValue: 0,
            clientComment: "",
            lastVerified: today.toISOString(),
            lastVerifiedBy: myself.value,
          },
    };

    await ClientsService.addKeyContact(Number(relationshipReviewId), body)
      .then((response) => {
        dispatch(addToast({ mode: "success", message: response.message }));
        dispatch(setNeedUpdateKeyContactList(true));
        dispatch(setContact({ contact: null }));
        navigate(routeBack, {
          state: {
            from:
              location.state?.from === ROUTE_CLIENTS_RELATIONSHIP_REVIEW
                ? ROUTE_CLIENTS_RELATIONSHIP_REVIEW
                : "",
          },
        });
      })
      .catch(() => {
        dispatch(
          addToast({
            mode: "error",
            message: "Something wrong happened adding this key contact.",
          })
        );
        setActionLoading(false);
      });
    eventHandler.dispatch("toast action", { isClosed: false });
  };

  const handleEditKeyContact = async (
    event: any,
    viewNextKeyContact: boolean
  ) => {
    event.preventDefault();
    setActionLoading(true);

    // When user click "Save & View Next Contact" without touching form
    if (!formTouched) {
      setActionLoading(false);
      customNavigate(viewNextKeyContact);
      return;
    }

    let body: any = {
      contactId: actualKeyContactData.contact?.id,
      primaryRelationshipStewardId: keyContactForm.primaryRelationshipStewardId,
      relationshipGrowthPlan: formFields.relationshipGrowthPlan
        ? {
            growthPlan: keyContactForm.relationshipGrowthPlan,
            behaviorsAndConcerns: keyContactForm.behaviorsAndConcerns,
            lastVerified:
              keyContactForm.relationshipLastVerified?.toISOString(),
          }
        : null,
      advocacyLevel: formFields.advocacyLevel
        ? {
            // ASUITE-2107: N/A in Database has a new id equal to 5 to allow save
            level:
              keyContactForm.advocacyLevel === 0
                ? 5
                : keyContactForm.advocacyLevel,
            relationshipLevelTrendId: keyContactForm.advocacyTrending,
            lastVerified: keyContactForm.advocacyLastVerified?.toISOString(),
          }
        : null,
      stakeholderConfidence: formFields.stakeholderConfidence
        ? {
            confidenceValue: keyContactForm.stakeholderConfidence,
            clientComment: keyContactForm.stakeholderClientComment,
            lastVerified: keyContactForm.stakeholderLastVerified?.toISOString(),
            lastVerifiedBy: keyContactForm.stakeholderLastVerifiedBy,
          }
        : null,
    };

    await ClientsService.editKeyContact(
      Number(relationshipReviewId),
      Number(keyContactId),
      body
    )
      .then((response) => {
        dispatch(addToast({ mode: "success", message: response.message }));
        dispatch(setNeedUpdateKeyContactList(true));
        if (viewNextKeyContact) {
          setActionLoading(false);
        }
        customNavigate(viewNextKeyContact);
      })
      .catch(() => {
        dispatch(
          addToast({
            mode: "error",
            message: "Something wrong happened editing this key contact.",
          })
        );
        setActionLoading(false);
      });
    eventHandler.dispatch("toast action", { isClosed: false });
  };

  const handleRemoveContact = async (event: any) => {
    event.preventDefault();

    await clientDeleteKeyContact({
      relationshipId: Number(relationshipReviewId),
      keyContactId: Number(keyContactId),
      name: `${actualKeyContactData.contact?.firstName} ${actualKeyContactData.contact?.lastName}`,
    })
      .then(() => {
        dispatch(setNeedUpdateKeyContactList(true));
        navigate(routeBack, {
          state: {
            from:
              location.state?.from === ROUTE_CLIENTS_RELATIONSHIP_REVIEW
                ? ROUTE_CLIENTS_RELATIONSHIP_REVIEW
                : "",
          },
        });
        return true;
      })
      .catch(() => {
        dispatch(
          addToast({
            mode: "error",
            message:
              "Something wrong happened removing this key contact form this client.",
          })
        );
        setActionLoading(false);
      });
  };

  const isAbletoAdd = () => {
    let actualRelationship = clientRelationshipReview.find(
      (rr) => rr.id === Number(relationshipReviewId)
    );
    return !actualKeyContactData.isRelationshipReviewActive ||
      !isLogged ||
      !ClientForHeaderInfo?.isActive
      ? false
      : verifyModuleAccess(
          true,
          [ROLE_SUPER_ADMIN, ROLE_CLIENT_ADMIN],
          userRolesList
        ) ||
          (actualCompanyOwnerUserId === activeUserId &&
            verifyModuleAccess(true, [ROLE_BUSINESS_DEVELOPMENT_LEAD], userRolesList)) ||
          (verifyModuleAccess(true, [ROLE_SUPERVISOR], userRolesList) &&
            costCenterOwnedIds.includes(actualRelationship?.costCenterId));
  };

  const isEditable = () => {
    return isAbletoAdd() || isUserPrimaryRelationshipSteward;
  };

  const isPageLoading =
    isLoadingActualKeyContactData ||
    isLoadingKeyContacts ||
    actionLoading ||
    !rolesLoaded;

  const renderStickyActionButtons = () => (
    <div className="sticky-row">
      <div
        className="button-header"
        style={{ width: "100%", padding: "20px 15px 0px 45px" }}
      >
        <Link
          className="back-button-container d-flex text-decoration-none"
          to={routeBack}
          state={{
            from:
              location.state?.from === ROUTE_CLIENTS_RELATIONSHIP_REVIEW
                ? ROUTE_CLIENTS_RELATIONSHIP_REVIEW
                : "",
          }}
        >
          <BiLeftArrowAlt className="back-button-arrow mt-1" />
          <span className="back-button-text mt-1">
            Back to Key Contact List
          </span>
          {!actualKeyContactData.isRelationshipReviewActive && (
            <button className="btn-status">Status: Inactive</button>
          )}
        </Link>

        {!isEditMode && contact?.isActive && isAbletoAdd() && (
          <button
            className="btn btn-primary me-3"
            type="submit"
            disabled={!isFormValid}
            onClick={(e) => handleAddKeyContact(e)}
          >
            Add New Key Contact
          </button>
        )}

        {isEditable() && isEditMode && (
          <div>
            {actualKeyContactData.contact?.active && (
              <button
                className="btn btn-primary me-3"
                type="submit"
                disabled={!isFormValid || (isFormValid && !formTouched)}
                onClick={(e) => handleEditKeyContact(e, false)}
              >
                Save Changes
              </button>
            )}

            {actualKeyContactData.contact?.active && (
              <button
                className="btn button-secondary me-3"
                type="submit"
                onClick={(e) => handleEditKeyContact(e, true)}
                disabled={formTouched && !isFormValid}
              >
                Save & View Next Contact
              </button>
            )}

            <button
              className="btn button-secondary-danger"
              onClick={(e) => handleRemoveContact(e)}
            >
              Remove Contact
            </button>
          </div>
        )}
      </div>
    </div>
  );

  const renderContactDetailsPanel = () =>
    (isEditMode ? actualKeyContactData : contact) && (
      <div className="block block-sticky me-3 mt-4">
        <div className="block-header">
          <h6 style={{ fontWeight: "bold" }}>Contact Details</h6>
          <div className="d-flex justify-content-between">
            <Link
              className="link-text w-100"
              to={ROUTE_CLIENTS_CONTACTS_PROFILE.replace(
                ":id",
                id ?? ""
              ).replace(
                ":contactId",
                isEditMode ? actualKeyContactData.contact?.id : contact?.id
              )}
            >
              <span className="me-1">Edit Contact Fields</span>
            </Link>
          </div>
        </div>
        <div className="block-header">
          <div className="key-contact">
            <div className="key-contact-name d-flex mb-2">
              <div>
                <h5>
                  {`${
                    isEditMode
                      ? actualKeyContactData.contact?.firstName
                      : contact?.firstName
                  } ${
                    isEditMode
                      ? actualKeyContactData.contact?.lastName
                      : contact?.lastName
                  }`}{" "}
                  {isEditMode
                    ? !actualKeyContactData.contact?.active
                    : !contact?.isActive && (
                        <span>
                          <i> - (Inactive)</i>
                        </span>
                      )}
                </h5>
                {(isEditMode
                  ? actualKeyContactData.contact?.title
                  : contact?.title) && (
                  <div className="key-contact-name grayMedium">
                    <span>
                      {isEditMode
                        ? actualKeyContactData.contact?.title
                        : contact?.title}
                    </span>
                  </div>
                )}
              </div>
            </div>
            {(isEditMode
              ? actualKeyContactData.contact?.email
              : contact?.email) && (
              <div className="d-flex mt-3">
                <FiMail className="info-icon ms-2" />
                <div className="grayMedium">
                  {isEditMode
                    ? actualKeyContactData.contact?.email
                    : contact?.email}
                </div>
              </div>
            )}
            {(isEditMode
              ? actualKeyContactData.contact?.phoneNumber
              : contact?.phoneNumber) && (
              <div className="d-flex mt-3">
                <FiPhoneCall className="info-icon ms-2" />
                <div className="grayMedium">
                  {isEditMode
                    ? actualKeyContactData.contact?.phoneNumber
                    : contact?.phoneNumber}
                </div>
              </div>
            )}
          </div>
          <div className="block-header p-3"></div>
          <div className="mt-3">
            <div className="position-relative">
              <label
                className="form-label grayMedium"
                htmlFor="primaryRelationshipStewardSelect"
              >
                Primary Relationship Steward
              </label>
              <Select
                id="primaryRelationshipStewardSelect"
                placeholder="Select Name"
                className="mb-3"
                options={users}
                isLoading={!metaBillingLoaded}
                value={{
                  value: keyContactForm.primaryRelationshipStewardId,
                  label: getUserNameToCompleteSelect(
                    users,
                    keyContactForm.primaryRelationshipStewardId
                  ),
                }}
                onChange={(kcr: any) => {
                  handleFormChange(
                    kcr.value,
                    "primaryRelationshipStewardId"
                  );
                }}
                isDisabled={!isEditable()}
              />
            </div>
          </div>
          <div className="d-flex mt-4">
            <div className="grayMedium w-40">Roles</div>
            <div className="d-flex flex-column">
              {isEditMode &&
                actualKeyContactData.contact?.roles.map((role: string) => (
                  <span
                    key={actualKeyContactData.contact?.id + role}
                    className="key-contact-role-block me-1 mb-1 text-center"
                  >
                    {role}
                  </span>
                ))}
              {!isEditMode &&
                contact?.roles.map((role: string) => (
                  <span
                    key={contact?.id + role}
                    className="key-contact-role-block me-1 mb-1 text-center"
                  >
                    {role}
                  </span>
                ))}
            </div>
          </div>
          <div className="d-flex mt-4">
            <div className="grayMedium w-40">
              DISC profile{" "}
              <BiInfoCircle className="disc-icon" onClick={showModalProfiles} />
            </div>
            {(isEditMode
              ? actualKeyContactData.contact?.discProfile
              : contact?.discProfile) && (
              <span className="key-contact-profile-block">
                {isEditMode
                  ? actualKeyContactData.contact?.discProfile
                  : contact?.discProfile}
              </span>
            )}
          </div>
          <div className="d-flex mt-4">
            <div className="grayMedium w-40">Relationship status</div>
            {isEditMode &&
              actualKeyContactData.advocacyLevel &&
              actualKeyContactData.advocacyLevel.level < 5 && (
                <span
                  className={`key-contact-role-block ${getAdvocacyNumber(
                    actualKeyContactData.advocacyLevel
                      ? actualKeyContactData.advocacyLevel.level
                      : 0
                  )}`}
                >
                  {getAdvocacyNumber(
                    actualKeyContactData.advocacyLevel
                      ? actualKeyContactData.advocacyLevel.level
                      : 0
                  )}
                  <span className="trend-arrow">
                    {getRelationshipTrend(
                      actualKeyContactData.advocacyLevel.relationshipLevelTrendId
                    )}
                  </span>
                </span>
              )}
          </div>
        </div>
      </div>
    );

  const renderRelationshipLevelBlock = () => (
    <div className="block block me-3 mt-4">
      <div className="block-header block-header-with-background">
        <h6 style={{ fontWeight: "bold" }}>Relationship Level</h6>
        <div>This is the latest advocacy level exhibited by this contact.</div>
      </div>
      <div className="block-body">
        <div className="d-flex justify-content-between">
          <div className="d-flex flex-column" style={{ width: "65%" }}>
            <label className="form-label gray-label" htmlFor="advocacy-slider">
              LEVEL
            </label>
            <Slider
              id="advocacy-slider"
              key="advocacy-slider"
              disabled={!isEditable()}
              value={keyContactForm.advocacyLevel ? keyContactForm.advocacyLevel : 0}
              step={1}
              min={0}
              max={4}
              onChange={(e) => handleFormChange(e, "advocacyLevel")}
            />
            <div className="slider-indicators">
              <span>N/A</span>
              <span>Below Trust</span>
              <span>Trust</span>
              <span>Loyalty</span>
              <span>Advocacy</span>
            </div>
          </div>
          <div className="d-flex flex-column" style={{ width: "25%" }}>
            <label
              className="form-label gray-label"
              htmlFor="advocacyTrendingSelect"
            >
              TRENDING
            </label>
            <Select
              id="advocacyTrendingSelect"
              placeholder="Select Name"
              className="mb-2"
              options={trendingOptions}
              value={{
                value: keyContactForm.advocacyTrending,
                label: getTrendingName(keyContactForm.advocacyTrending),
              }}
              onChange={(rlt: any) => {
                handleFormChange(rlt.value, "relationshipLevelTrendId");
              }}
              isDisabled={!isEditable()}
            />
          </div>
        </div>
        <div className="advocacy-description mt-5">
          <p className="grayMedium">
            {setAdvocacyDescription(keyContactForm.advocacyLevel)}
          </p>
          <p className="form-label gray-label">Behaviors Exhibited</p>
          <ul className="list-item">
            {setAdvocacyTexts(keyContactForm.advocacyLevel).map(
              (elem: string) => (
                <li key={elem}>{elem}</li>
              )
            )}
          </ul>
        </div>
        <div className="d-flex flex-column mt-3">
          <label className="form-label gray-label" htmlFor="advocacyLastVerified">
            LAST VERIFIED ON
          </label>
          <div style={{ width: "30%" }}>
            <Calendar
              date={keyContactForm.advocacyLastVerified}
              isDisabled={!isEditable()}
              onChange={(e) => handleFormChange(e, "advocacyLastVerified")}
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderGrowthPlanBlock = () => (
    <div className="block mt-5">
      <div className="block-header block-header-with-background">
        <h6 style={{ fontWeight: "bold" }}>Relationship Growth Plan</h6>
        <div>Enter the plan to grow the relationship with this contact.</div>
      </div>
      <div className="block-body">
        <div className="d-flex flex-column mb-3">
          <label
            className="form-label gray-label"
            htmlFor="behaviorsAndConcerns"
          >
            Behaviors and Concerns
          </label>
          <TextareaAutosize
            id="behaviorsAndConcerns"
            minRows={3}
            maxRows={6}
            className="textarea-resize"
            value={keyContactForm.behaviorsAndConcerns}
            disabled={!isEditable()}
            onChange={(e) => handleFormChange(e, "behaviorsAndConcerns")}
          />
        </div>
        <div className="d-flex flex-column">
          <label
            htmlFor="relationship-growth-plan"
            className="form-label gray-label"
          >
            RELATIONSHIP GROWTH PLAN
          </label>
          <TextareaAutosize
            id="relationship-growth-plan"
            minRows={3}
            maxRows={6}
            className="textarea-resize"
            value={keyContactForm.relationshipGrowthPlan}
            disabled={!isEditable()}
            onChange={(e) => handleFormChange(e, "relationshipGrowthPlan")}
          />
        </div>
        <div className="d-flex flex-column mt-3">
          <label
            htmlFor="relationship-last-verified"
            className="form-label gray-label"
          >
            LAST VERIFIED ON
          </label>
          <div style={{ width: "40%" }}>
            <Calendar
              id="relationship-last-verified"
              date={keyContactForm.relationshipLastVerified}
              isDisabled={!isEditable()}
              onChange={(e) =>
                handleFormChange(e, "relationshipLastVerified")
              }
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderStakeholderConfidenceBlock = () => (
    <div className="block mt-5">
      <div className="block-header block-header-with-background">
        <h6 style={{ fontWeight: "bold" }}>Stakeholder Confidence</h6>
        <div>
          Direct client stakeholder confidence in the team’s performance.
          Should be a quick live conversation and when that isn’t possible
          then an email with a requested response time
        </div>
      </div>
      <div className="block-body">
        <div className="d-flex flex-column">
          <label
            className="form-label gray-label"
            htmlFor="stakeholderConfidenceSlider"
          >
            LEVEL
          </label>
          <Slider
            id="stakeholderConfidenceSlider"
            key="stackholder-slider"
            disabled={!isEditable()}
            style={{ width: "95%", margin: "auto" }}
            value={
              keyContactForm.stakeholderConfidence
                ? keyContactForm.stakeholderConfidence
                : 0
            }
            step={1}
            min={0}
            max={10}
            onChange={(e) => handleFormChange(e, "stakeholderConfidence")}
          />
          <div className="slider-indicators" style={{ width: "100%" }}>
            <span>N/A</span>
            <span>1</span>
            <span>2</span>
            <span>3</span>
            <span>4</span>
            <span>5</span>
            <span>6</span>
            <span>7</span>
            <span>8</span>
            <span>9</span>
            <span>10</span>
          </div>
          <div className="d-flex flex-column mt-3">
            <label
              className="form-label gray-label"
              htmlFor="stakeholderClientComment"
            >
              CLIENT COMMENT
              {keyContactForm.stakeholderClientComment.length === 0 &&
              formFields.stakeholderConfidence ? (
                <span style={{ color: "red" }}> *</span>
              ) : (
                ""
              )}
            </label>
            <TextareaAutosize
              id="stakeholderClientComment"
              minRows={3}
              maxRows={6}
              className="textarea-resize"
              value={keyContactForm.stakeholderClientComment}
              disabled={!isEditable()}
              onChange={(e) =>
                handleFormChange(e, "stakeholderClientComment")
              }
            />
          </div>
          <div className="d-flex mt-3 justify-content-between">
            <div style={{ width: "30%" }} className="me-3">
              <label
                className="form-label gray-label"
                htmlFor="stakeholderLastVerified"
              >
                LAST VERIFIED ON
              </label>
              <Calendar
                date={keyContactForm.stakeholderLastVerified}
                isDisabled={!isEditable()}
                onChange={(e) =>
                  handleFormChange(e, "stakeholderLastVerified")
                }
              />
            </div>
            <div style={{ width: "30%" }}>
              <label
                className="form-label gray-label"
                htmlFor="stakeholderLastVerifiedBy"
              >
                LAST VERIFIED BY
              </label>
              <Select
                id="stakeholderLastVerifiedBy"
                placeholder="Select Name"
                className="mb-2"
                options={users}
                isLoading={!metaBillingLoaded}
                value={{
                  value: keyContactForm.stakeholderLastVerifiedBy,
                  label: getUserNameToCompleteSelect(
                    users,
                    keyContactForm.stakeholderLastVerifiedBy
                  ),
                }}
                onChange={(lvb: any) => {
                  handleFormChange(lvb.value, "stakeholderLastVerifiedBy");
                }}
                isDisabled={!isEditable()}
              />
            </div>
            {!actualKeyContactData.confidenceHistory && (
              <div className="link-text mt-4"></div>
            )}
            {isEditMode && actualKeyContactData.confidenceHistory && (
              <Link
                className="link-text"
                style={{ marginTop: "4%" }}
                to={confidenceHistoryLink}
                state={{
                  from:
                    location.state?.from === ROUTE_CLIENTS_RELATIONSHIP_REVIEW
                      ? ROUTE_CLIENTS_RELATIONSHIP_REVIEW
                      : "",
                }}
              >
                CONFIDENCE HISTORY
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Modal closeModal={closeModal} {...modalProps} />
      <ModalPortal ref={modalRef}>
        <ModalImage btnCancel={closeModalProfiles} type="formModal" />
      </ModalPortal>
      <div className="content">
        <HeaderClientsModule showBackButton={false} isLoadingPage={isPageLoading} />

        {isPageLoading && (
          <Spinner
            style={{
              marginLeft: "50%",
              marginTop: "5%",
            }}
          />
        )}

        {!isPageLoading &&
          !verifyModuleAccess(true, CLIENTS_ALL_ROLES, userRolesList) && (
            <ForbiddenPage />
          )}

        {!isPageLoading &&
          verifyModuleAccess(true, CLIENTS_ALL_ROLES, userRolesList) && (
            <form>
              {renderStickyActionButtons()}

              {/* Contact Detail right panel */}
              {renderContactDetailsPanel()}

              {/* Advocacy Level => Relationship Level */}
              {renderRelationshipLevelBlock()}

              {/* Relationship Growth Plan */}
              {renderGrowthPlanBlock()}

              {/* Stakeholder Confidence */}
              {renderStakeholderConfidenceBlock()}
            </form>
          )}
        <Footer />
      </div>
    </>
  );
};
